import { PROFILE_NODE_NAMES } from '@/services/constraints/constants'
import {
  hasPublicTag,
  isTrackedPublicSymptom,
  KNOWN_TAGS,
  trackingCycleNeverRegex,
} from '@/services/constraints/tags'
import { VariableType } from '@/services/models/enums'
import { Variable } from '@/types/variable'

const REQUIRED_STATE_NAMES_BY_TYPE: Record<string, readonly string[]> = {
  [VariableType.Symptom]: ['Absent', 'Present'],
  [VariableType.MedicationOrSupplement]: ['Absent', 'Present'],
  [VariableType.Diagnosis]: ['Negative', 'Positive'],
}

export enum NetworkExportErrorTypes {
  UNKNOWN_HASHTAG = 'UNKNOWN_HASHTAG',
  INVALID_HASHTAG = 'INVALID_HASHTAG',
  MISSING_HASHTAG = 'MISSING_HASHTAG',
  DUPLICATE_HASHTAG = 'DUPLICATE_HASHTAG',
  INCONSISTENT_FAULTS = 'INCONSISTENT_FAULTS',
  NON_PUBLISHABLE_PUBLIC_VARIABLE = 'NON_PUBLISHABLE_PUBLIC_VARIABLE',
  MISSING_DIAGNOSIS_NODE = 'MISSING_DIAGNOSIS_NODE',
  MULTIPLE_DIAGNOSIS_NODES = 'MULTIPLE_DIAGNOSIS_NODES',
  INVALID_DIAGNOSIS_NAME = 'INVALID_DIAGNOSIS_NAME',
  DISEASE_DIAGNOSIS_PUBLIC_MISMATCH = 'DISEASE_DIAGNOSIS_PUBLIC_MISMATCH',
  BROKEN_LINK = 'BROKEN_LINK',
  INVALID_STATE_NAMES = 'INVALID_STATE_NAMES',
  NON_DISCRETE_PUBLIC_VARIABLE = 'NON_DISCRETE_PUBLIC_VARIABLE',
  MISSING_OPTIMAL_STATE = 'MISSING_OPTIMAL_STATE',
  NON_SEMICONTINUOUS_TRACKED_SYMPTOM = 'NON_SEMICONTINUOUS_TRACKED_SYMPTOM',
}

export type NetworkExportErrors =
  | {
      type: NetworkExportErrorTypes.UNKNOWN_HASHTAG
      variableName: string
      tagFound: string
    }
  | {
      type: NetworkExportErrorTypes.INVALID_HASHTAG
      variableName: string
      tagFound: string
    }
  | {
      type: NetworkExportErrorTypes.MISSING_HASHTAG
      variableName: string
      missingTag: string
      variableType: string
    }
  | {
      type: NetworkExportErrorTypes.DUPLICATE_HASHTAG
      variableName: string
      duplicateTag: string
      tagsFound: string[]
    }
  | {
      type: NetworkExportErrorTypes.INCONSISTENT_FAULTS
      variableName: string
      states: string[]
      inconsistentState: string
    }
  | {
      type: NetworkExportErrorTypes.NON_PUBLISHABLE_PUBLIC_VARIABLE
      variableName: string
      variableType: string
    }
  | {
      type: NetworkExportErrorTypes.MISSING_DIAGNOSIS_NODE
      diseaseName: string
    }
  | {
      type: NetworkExportErrorTypes.MULTIPLE_DIAGNOSIS_NODES
      diseaseName: string
      diagnosisNames: string[]
    }
  | {
      type: NetworkExportErrorTypes.INVALID_DIAGNOSIS_NAME
      diseaseName: string
      diagnosisName: string
    }
  | {
      type: NetworkExportErrorTypes.DISEASE_DIAGNOSIS_PUBLIC_MISMATCH
      diseaseName: string
      diagnosisName: string
    }
  | {
      type: NetworkExportErrorTypes.BROKEN_LINK
      variableName: string
      url: string
    }
  | {
      type: NetworkExportErrorTypes.INVALID_STATE_NAMES
      variableName: string
      stateNames: string[]
    }
  | {
      type: NetworkExportErrorTypes.NON_DISCRETE_PUBLIC_VARIABLE
      variableName: string
      variableType: string
    }
  | {
      type: NetworkExportErrorTypes.MISSING_OPTIMAL_STATE
      variableName: string
    }
  | {
      type: NetworkExportErrorTypes.NON_SEMICONTINUOUS_TRACKED_SYMPTOM
      variableName: string
    }

function isSemiContinuous(variable: Variable): boolean {
  return (variable.intervals?.length ?? 0) > 0
}

export function getReleaseErrorsForAVariable(
  variable: Variable & { tags: string[] },
  stateIdToNameMap: Map<string, string> = new Map(),
) {
  const errors: Array<NetworkExportErrors> = []

  if (variable.faults) {
    const faultErrors = getFaultErrors(
      variable.faults,
      variable.stateIds,
      variable.name,
    )
    errors.push(...faultErrors)
  }

  const hashtagErrors = getHashtagErrors(
    variable.tags,
    variable.type.name,
    variable.name,
  )
  errors.push(...hashtagErrors)

  const publishableErrors = getPublishableErrors(
    variable.tags,
    variable.type,
    variable.name,
  )
  errors.push(...publishableErrors)

  const stateNameErrors = getStateNameErrors(variable, stateIdToNameMap)
  errors.push(...stateNameErrors)

  const discreteErrors = getDiscreteErrors(variable)
  errors.push(...discreteErrors)

  const optimalStateErrors = getOptimalStateErrors(variable)
  errors.push(...optimalStateErrors)

  const trackedSymptomErrors = getTrackedSymptomErrors(variable)
  errors.push(...trackedSymptomErrors)

  return errors
}

function getFaultErrors(
  faults: string[],
  states: string[],
  variableName: string,
): Array<NetworkExportErrors> {
  return faults.flatMap((f) => {
    if (!states.includes(f)) {
      return [
        {
          type: NetworkExportErrorTypes.INCONSISTENT_FAULTS,
          variableName: variableName,
          states: states,
          inconsistentState: f,
        },
      ]
    }
    return []
  })
}

function getHashtagErrors(
  tags: string[],
  variableTypeName: string,
  variableName: string,
): Array<NetworkExportErrors> {
  const errors: Array<NetworkExportErrors> = []
  const knownTags = Object.values(KNOWN_TAGS)
  const knownTagsRegExps = Object.values(KNOWN_TAGS).flatMap(
    (knownTag) => knownTag.regExps,
  )
  const tagsWithBase = knownTags.filter(
    (tagWithBase) => 'baseOfTheTag' in tagWithBase,
  )

  tags.forEach((tag) => {
    const isValid = knownTagsRegExps.some((regExp) => regExp.test(tag))
    if (!isValid) {
      const isPartiallyValid = tagsWithBase.some((tagWithBase) =>
        tagWithBase.baseOfTheTag.test(tag),
      )
      if (isPartiallyValid) {
        errors.push({
          type: NetworkExportErrorTypes.INVALID_HASHTAG,
          variableName: variableName,
          tagFound: tag,
        })
      } else {
        errors.push({
          type: NetworkExportErrorTypes.UNKNOWN_HASHTAG,
          variableName: variableName,
          tagFound: tag,
        })
      }
    }
  })

  const singleValuedTags = knownTags.filter(
    (knownTag) => 'singleValued' in knownTag,
  )
  singleValuedTags.forEach((singleValuedTag) => {
    const tagsFound = tags.filter((tag) =>
      singleValuedTag.baseOfTheTag.test(tag),
    )
    if (tagsFound.length > 1) {
      errors.push({
        type: NetworkExportErrorTypes.DUPLICATE_HASHTAG,
        variableName: variableName,
        duplicateTag: singleValuedTag.name,
        tagsFound: tagsFound,
      })
    }
  })

  const tagsWithRequiredCheck = knownTags.filter(
    (knownTag) => 'checkIsRequired' in knownTag,
  )
  tagsWithRequiredCheck.forEach((tagWithRequiredCheck) => {
    if (
      tagWithRequiredCheck.checkIsRequired(
        tags,
        variableTypeName,
        variableName,
      ) &&
      !tags.some((tag) => tagWithRequiredCheck.baseOfTheTag.test(tag))
    ) {
      errors.push({
        type: NetworkExportErrorTypes.MISSING_HASHTAG,
        variableName: variableName,
        missingTag: tagWithRequiredCheck.name,
        variableType: variableTypeName,
      })
    }
  })

  return errors
}

function getPublishableErrors(
  tags: string[],
  variableType: Variable['type'],
  variableName: string,
): Array<NetworkExportErrors> {
  const errors: Array<NetworkExportErrors> = []

  if (hasPublicTag(tags) && !variableType.isPublishable) {
    errors.push({
      type: NetworkExportErrorTypes.NON_PUBLISHABLE_PUBLIC_VARIABLE,
      variableName: variableName,
      variableType: variableType.name,
    })
  }

  return errors
}

function getStateNameErrors(
  variable: Variable & { tags: string[] },
  stateIdToNameMap: Map<string, string>,
): Array<NetworkExportErrors> {
  if (!hasPublicTag(variable.tags)) {
    return []
  }

  // Only validate state names for Symptom, Diagnosis, and Medication/Supplement
  if (
    variable.type.name !== VariableType.Symptom &&
    variable.type.name !== VariableType.Diagnosis &&
    variable.type.name !== VariableType.MedicationOrSupplement
  ) {
    return []
  }

  // Semi-continuous Symptoms can have any state names
  if (
    variable.type.name === VariableType.Symptom &&
    isSemiContinuous(variable)
  ) {
    return []
  }

  const stateNames = variable.stateIds.map((stateId) => {
    const stateName = stateIdToNameMap.get(stateId)
    if (stateName === undefined) {
      throw new Error(
        `State name not found for variable "${variable.name}" (stateId: ${stateId}) during release validation.`,
      )
    }
    return stateName
  })

  const requiredStateNames = REQUIRED_STATE_NAMES_BY_TYPE[variable.type.name]
  if (!requiredStateNames) {
    return []
  }
  const allowedStateNames = new Set(requiredStateNames)
  const hasOnlyAllowedNames = stateNames.every((name) =>
    allowedStateNames.has(name),
  )
  const hasAllRequiredNames = requiredStateNames.every((requiredName) =>
    stateNames.includes(requiredName),
  )

  if (hasOnlyAllowedNames && hasAllRequiredNames) {
    return []
  }

  return [
    {
      type: NetworkExportErrorTypes.INVALID_STATE_NAMES,
      variableName: variable.name,
      stateNames,
    },
  ]
}

function getDiscreteErrors(
  variable: Variable & { tags: string[] },
): Array<NetworkExportErrors> {
  if (!hasPublicTag(variable.tags)) {
    return []
  }

  if (variable.type.name === VariableType.Symptom) {
    return []
  }

  if (
    variable.type.name !== VariableType.Diagnosis &&
    variable.type.name !== VariableType.MedicationOrSupplement
  ) {
    return []
  }

  if (!isSemiContinuous(variable)) {
    return []
  }

  return [
    {
      type: NetworkExportErrorTypes.NON_DISCRETE_PUBLIC_VARIABLE,
      variableName: variable.name,
      variableType: variable.type.name,
    },
  ]
}

function getOptimalStateErrors(
  variable: Variable & { tags: string[] },
): Array<NetworkExportErrors> {
  if (!hasPublicTag(variable.tags)) {
    return []
  }

  if (PROFILE_NODE_NAMES.includes(variable.name)) {
    return []
  }

  const hasTrackingNever = variable.tags.some((tag) =>
    trackingCycleNeverRegex.test(tag),
  )
  if (hasTrackingNever) {
    return []
  }

  if (variable.optimalStateId !== null) {
    return []
  }

  return [
    {
      type: NetworkExportErrorTypes.MISSING_OPTIMAL_STATE,
      variableName: variable.name,
    },
  ]
}

// A public Symptom that is tracked (tracking-cycle other than "never") must be
// semicontinuous so Ancestra Health can record measured values against it.
function getTrackedSymptomErrors(
  variable: Variable & { tags: string[] },
): Array<NetworkExportErrors> {
  if (!isTrackedPublicSymptom(variable.tags, variable.type.name)) {
    return []
  }

  if (isSemiContinuous(variable)) {
    return []
  }

  return [
    {
      type: NetworkExportErrorTypes.NON_SEMICONTINUOUS_TRACKED_SYMPTOM,
      variableName: variable.name,
    },
  ]
}
